import { memo as Memo, useMemo, useState, useCallback, useEffect, useRef } from 'react';
import gsap from 'gsap';

//* HOCs
import { withUIContext } from 'context';

//* Components
import Container from 'components/common/Container';
import Text from 'components/common/Text';
import Image from 'components/common/Image';
import Icon from 'components/common/Icon';

//* Styles
import InformativeSliderStyle from './style';

const InformativeSlider = Memo(({ sliderItems, bgColor, winWidth }) => {
	//! Refs
	const bigSlideRefs = useRef([]);
	const smallSlideRefs = useRef([]);
	const infoItemRefs = useRef([]);
	const isAnimated = useRef(false);

	//! States
	const [active, setActive] = useState(0);
	const [activeInfo, setActiveInfo] = useState(0);
	const [disabledActionButton, setDisabledActionButton] = useState(1);
	const [infoItemHeight, setInfoItemHeight] = useState(0);

	//! Items
	const [contents, bigSlides, smallSlides, paginationItems] = useMemo(() => {
		const bigSlidesArr = [];
		const smallSlidesArr = [];
		const paginationArr = [];

		const contentsArr = sliderItems.map((item, k) => {
			const bigSlideClass = active === k ? 'active-slide' : active > k ? 'prev-slide' : 'next-slide';
			const smallSlideClass = active + 1 === k ? 'active-slide' : active + 1 > k ? 'prev-slide' : 'next-slide';

			bigSlidesArr.push(
				<div
					key={k}
					ref={(ref) => !bigSlideRefs.current[k] && bigSlideRefs.current.push(ref)}
					className={`is-big-image ${bigSlideClass} bg${bgColor}`}>
					<Image
						src={item.image.url}
						alt={item.image.alt}
						objectFit={'cover'}
					/>
				</div>
			);

			smallSlidesArr.push(
				<div
					key={k}
					ref={(ref) => !smallSlideRefs.current[k] && smallSlideRefs.current.push(ref)}
					className={`is-small-image ${smallSlideClass} bg${bgColor}`}>
					<Image
						src={item.image.url}
						alt={item.image.alt}
						objectFit={'cover'}
					/>
				</div>
			);

			paginationArr.push(
				<span
					key={k}
					onClick={() => handleSlideChange(false, k)}
					className={`pagination-item bg-${bgColor} ${activeInfo === k ? 'active' : ''}`}></span>
			);

			return (
				<div
					key={k}
					ref={(ref) => !infoItemRefs.current[k] && infoItemRefs.current.push(ref)}
					className={`is-info ${k === activeInfo ? 'active' : ''}`}>
					<Text
						className='font-soundscape h6'
						text={String(k + 1).padStart(2, '0')}
					/>
					{item.title && (
						<Text
							tag={'h2'}
							className='font-ping-bolder h3 is-info-title'
							text={item.title}
						/>
					)}
					{item.description && (
						<Text
							tag={`h3`}
							className='font-helvetica p is-info-desc'
							text={item.description}
						/>
					)}
				</div>
			);
		});

		smallSlidesArr.push(
			<div
				ref={(ref) => !smallSlideRefs.current[sliderItems.length] && smallSlideRefs.current.push(ref)}
				key={sliderItems.length}
				className={`is-small-image next-slide bg${bgColor}`}></div>
		);

		return [contentsArr, bigSlidesArr, smallSlidesArr, paginationArr];
	}, [active, activeInfo, bgColor, bigSlideRefs.current, smallSlideRefs.current]);

	//! Handle slide change
	const handleSlideChange = useCallback(
		(isPrev, index) => {
			const nextSlide = typeof index === 'number' ? index : isPrev ? active - 1 : active + 1;
			const nextSmallSlide = isPrev ? nextSlide + 1 : nextSlide + 1;

			if (nextSlide >= 0 && nextSlide < sliderItems.length && !isAnimated.current) {
				isAnimated.current = true;
				setActiveInfo(nextSlide);
				gsap.to([bigSlideRefs.current[nextSlide], smallSlideRefs.current[nextSmallSlide]], {
					duration: 0.5,
					webkitClipPath: `inset(0% 0% 0% 0%)`,
					clipPath: `inset(0% 0% 0% 0%)`,
					onComplete: () => {
						isAnimated.current = false;
						setActive(nextSlide);
					},
				});
			}

			const disableBtn = nextSlide === 0 ? 1 : nextSlide === sliderItems.length - 1 ? 2 : 0;

			disableBtn !== disabledActionButton && setDisabledActionButton(disableBtn);
		},
		[active, sliderItems, isAnimated.current, bigSlideRefs.current, smallSlideRefs.current]
	);

	//! Clear items inline style
	useEffect(() => {
		gsap.set([...bigSlideRefs.current, ...smallSlideRefs.current], { clearProps: 'all' });
	}, [active, bigSlideRefs.current, smallSlideRefs.current]);

	//! Info items height
	useEffect(() => {
		const newHeight = infoItemRefs.current.reduce((prevHeight, current) => {
			const height = current.getBoundingClientRect().height;
			return height > prevHeight ? height : prevHeight;
		}, 0);

		infoItemHeight !== newHeight && setInfoItemHeight(newHeight);
	}, [winWidth, infoItemRefs.current, infoItemHeight]);

	return (
		<InformativeSliderStyle
			infoItemHeight={infoItemHeight}
			className={`primaryColor3 bg${bgColor}`}>
			<Container
				className='is-container slid-container'
				row>
				<div className='col-6 col-ds-6 col-ts-10 col-m-12 is-info-container'>
					<div className='is-info-inner-container'>{contents}</div>
				</div>

				<div className='col-6 col-ds-6 col-ts-10 col-m-12 is-right-container'>
					<div className='is-big-image-container'>
						<div className='is-big-image-inner'>{bigSlides}</div>

						<div className='is-small-image-container'>
							<div className='is-small-image-inner'>{smallSlides}</div>
						</div>
					</div>
				</div>

				<div className='action-buttons'>
					<Icon
						name='arrow'
						onClick={() => handleSlideChange(true)}
						className={`${disabledActionButton === 1 || sliderItems.length === 1 ? 'disabled' : ''} arrow-left h5`}
					/>

					<Icon
						name='arrow'
						onClick={() => handleSlideChange()}
						className={`${disabledActionButton === 2 || sliderItems.length === 1 ? 'disabled' : ''} arrow-right h5`}
					/>
				</div>
			</Container>

			<div className={`pagination-container`}>{paginationItems}</div>
		</InformativeSliderStyle>
	);
});

export default withUIContext(InformativeSlider, ['winWidth']);
